import { useState } from "react";
import type { Platform } from "../api/labels";
import { platformHasPdpStage, platformLabel } from "../api/platforms";
import { Button, Grid, Input, Panel, Row, Stack } from "@akaike/ui";
import { PlayIcon } from "./foundation";

// Ad-hoc brand-sweep launch settings (planning/41). Mirrors the brand_sweep knobs the
// Jobs form exposes, so a one-off launch can be tuned (workers, mode, ...) just like a
// saved job. Blank fields fall back to the engine defaults; `mode` defaults to "full".

type NumField = {
  key: string;
  label: string;
  placeholder: string;
  hint?: string;
};

const CORE_FIELDS: readonly NumField[] = [
  { key: "num_workers", label: "Workers", placeholder: "16" },
  { key: "max_attempts", label: "Max attempts / search", placeholder: "30" },
  { key: "max_search_pages", label: "Max search pages", placeholder: "all" },
  { key: "request_timeout", label: "Request timeout (s)", placeholder: "8" },
];

const ADVANCED_FIELDS: readonly NumField[] = [
  {
    key: "proxy_max_calls",
    label: "Calls / proxy",
    placeholder: "40",
    hint: "Rotate to a fresh proxy after this many calls on one lease.",
  },
  {
    key: "proxy_max_calls_jitter",
    label: "Rotation jitter",
    placeholder: "5",
    hint: "+/- randomness on the rotation threshold so leases don't rotate in lockstep.",
  },
  {
    key: "proxy_max_consecutive_failures",
    label: "Max fails / proxy",
    placeholder: "3",
    hint: "Retire a lease after this many consecutive failures.",
  },
  {
    key: "store_limit",
    label: "Store limit (testing)",
    placeholder: "all",
    hint: "Biased [:N] prefix for a quick local run. The real coverage lever is Sampled mode.",
  },
];

export type SweepKnobs = Record<string, number | string>;

export function SweepConfigModal({
  scopeLabel,
  note,
  targetPlatforms,
  pending,
  pdpOnly = false,
  onStart,
  onClose,
}: {
  scopeLabel: string;
  note?: string;
  targetPlatforms: Platform[];
  pending: boolean;
  // No search term to crawl with: only the PDP refresh (re-read existing SKUs) can run.
  pdpOnly?: boolean;
  onStart: (knobs: SweepKnobs) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"full" | "sampled" | "pdp">(pdpOnly ? "pdp" : "full");
  const [vals, setVals] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const pdpPlatforms = targetPlatforms.filter(platformHasPdpStage);
  const noPdpPlatforms = targetPlatforms.filter((platform) => !platformHasPdpStage(platform));
  const pdpAvailable = pdpPlatforms.length > 0;
  const set = (k: string, v: string) => setVals((p) => ({ ...p, [k]: v }));

  const start = () => {
    const knobs: SweepKnobs = { mode };
    const addNum = (k: string) => {
      const raw = vals[k]?.trim();
      if (!raw) return;
      const n = Number(raw);
      if (!Number.isNaN(n)) knobs[k] = n;
    };
    if (mode === "sampled") addNum("store_sample");
    [...CORE_FIELDS, ...ADVANCED_FIELDS].forEach((f) => addNum(f.key));
    onStart(knobs);
  };

  const field = (f: NumField) => (
    <label key={f.key} className="col" style={{ gap: 4, minWidth: 0 }}>
      <span className="faint" style={{ fontSize: 11 }} title={f.hint}>
        {f.label}
      </span>
      <Input
        type="number"
        min={0}
        placeholder={f.placeholder}
        value={vals[f.key] ?? ""}
        onChange={(e) => set(f.key, e.currentTarget.value)}
      />
    </label>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <Panel
        style={{ width: "100%", maxWidth: 560 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head title="Configure sweep">
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">
            ✕
          </Button>
        </Panel.Head>
        <Panel.Body>
          <Stack gap="md">
          <Stack gap="xs">
            <span className="strong" style={{ fontSize: 13 }}>{scopeLabel}</span>
            {note && <span className="faint" style={{ fontSize: 11 }}>{note}</span>}
          </Stack>

          <label className="col" style={{ gap: 4 }}>
            <span className="faint" style={{ fontSize: 11 }}>Sweep mode</span>
            <select
              className="input"
              value={mode}
              onChange={(e) => setMode(e.currentTarget.value as "full" | "sampled" | "pdp")}
            >
              <option value="full" disabled={pdpOnly}>
                Full, every active store, authoritative (can retire)
              </option>
              <option value="sampled" disabled={pdpOnly}>
                Sampled: rotating store subset, additive (faster)
              </option>
              <option value="pdp" disabled={!pdpAvailable}>
                PDP refresh - product-page enrichment, no search
              </option>
            </select>
            {mode === "pdp" && (
              <span className="faint" style={{ fontSize: 11 }}>
                Refreshes enrichment + combo classification for existing SKUs from product pages.
                No search crawl, additive only. Runs on{" "}
                {pdpPlatforms.map(platformLabel).join(", ")}
                {noPdpPlatforms.length > 0
                  ? `; skips ${noPdpPlatforms.map(platformLabel).join(", ")} (no PDP stage).`
                  : "."}
              </span>
            )}
            {pdpOnly && (
              <span className="hint" style={{ marginTop: 4 }}>
                No enabled search term here, so only a PDP refresh can run. Add a search candidate
                to discover new SKUs.
              </span>
            )}
            {!pdpAvailable && (
              <span className="hint" style={{ marginTop: 4 }}>
                PDP refresh is unavailable for this target; none of its platforms expose a PDP stage.
              </span>
            )}
          </label>

          {mode === "sampled" && (
            <Grid cols={2} gap="sm">
              {field({
                key: "store_sample",
                label: "Store sample (K)",
                placeholder: "per-platform default",
                hint: "Stores searched per sampled run; rotates across runs.",
              })}
            </Grid>
          )}

          <Grid cols={2} gap="sm">
            {CORE_FIELDS.map(field)}
          </Grid>

            <Button variant="outline" size="sm" style={{ alignSelf: "flex-start" }} onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? "Hide advanced" : "Advanced (proxy rotation)"}
            </Button>
            {showAdvanced && (
              <Grid cols={2} gap="sm">
                {ADVANCED_FIELDS.map(field)}
              </Grid>
            )}

            <Row gap="sm" justify="end" style={{ marginTop: 4 }}>
              <Button variant="outline" disabled={pending} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" loading={pending} onClick={start}>
                {pending ? (
                  "Starting…"
                ) : (
                  <>
                    Start sweep <PlayIcon />
                  </>
                )}
              </Button>
            </Row>
          </Stack>
        </Panel.Body>
      </Panel>
    </div>
  );
}
